// @ts-nocheck
import { useState } from "react";
import { signupJambushrusti } from "@/services/jambushrustiAuth";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Library,
  Lock,
  Mail,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import {
  AuthField,
  AuthShell,
} from "@jambushrushti/site/components/auth/AuthShell";
import {
  isLibraryAdminAuthenticated,
  loginLibraryAdmin,
} from "@libraryadmin/services/auth";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignup, setIsSignup] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) {
      return;
    }
    if (!email.trim() || !password.trim()) {
      toast({
        title: "Missing login details",
        description: "Please enter your email or username and password.",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);

    try {
      if (isSignup) {
        await signupJambushrusti(email, password, "library");
        toast({
          title: "Account created",
          description: "Your Library account is ready. Please sign in.",
        });
        setIsSignup(false);
        setPassword("");
        setIsLoading(false);
        return;
      }
      await loginLibraryAdmin(email.trim(), password);
      setIsLoading(false);
      toast({
        title: "Welcome Back",
        description: "Logged in successfully",
      });
      const destination =
        location.state?.from || "/vault-library-admin/dashboard";
      navigate(destination, { replace: true });
    } catch (error) {
      setIsLoading(false);
      toast({
        title: isSignup ? "Unable to create account" : "Unable to sign in",
        description:
          error instanceof Error ? error.message : "Authentication failed.",
        variant: "destructive",
      });
    }
  };

  if (isLibraryAdminAuthenticated()) {
    return <Navigate to="/vault-library-admin/dashboard" replace />;
  }

  return (
    <AuthShell
      eyebrow="Library Administration"
      headline={["Jambushrushti", "Library"]}
      intro="Catalogue, issue and care for the books and rare manuscripts of the Muni Jambuvijayaji Research Center."
      icon={<Library />}
      title={isSignup ? "Create Library Account" : "Librarian Sign In"}
      subtitle="Jain Heritage & Manuscript Management"
    >
      <form onSubmit={handleLogin} noValidate className="au-form">
        <AuthField id="library-email" label="Email / Username" icon={<Mail />}>
          <input
            id="library-email"
            type="text"
            autoComplete="username"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="librarian@jambushrushti.org"
          />
        </AuthField>

        <AuthField
          id="library-password"
          label="Password"
          icon={<Lock />}
          action={
            <button
              type="button"
              className="au-link"
              onClick={() => {
                toast({
                  title: "Contact Superadmin",
                  description:
                    "Please contact your superadmin or IT administrator to reset your password.",
                });
              }}
            >
              Forgot?
            </button>
          }
          trailing={
            <button
              type="button"
              className="au-toggle"
              onClick={() => setShowPassword(current => !current)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <EyeOff aria-hidden="true" />
              ) : (
                <Eye aria-hidden="true" />
              )}
            </button>
          }
        >
          <input
            id="library-password"
            type={showPassword ? "text" : "password"}
            autoComplete={isSignup ? "new-password" : "current-password"}
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
          />
        </AuthField>

        <button type="submit" disabled={isLoading} className="au-submit">
          {isLoading ? (
            <>
              <Loader2 className="au-spin" aria-hidden="true" />
              {isSignup ? "Creating account..." : "Authenticating..."}
            </>
          ) : (
            <>
              {isSignup ? "Create Library Account" : "Sign In to Dashboard"}
              <ArrowRight aria-hidden="true" />
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
};

export default Login;
